import { usePortalScreen } from '../../app/telemetry/usePortalTelemetry';
import { portalScreenContext, trackPortalEvent } from '../../app/telemetry/portalTelemetry';
import { Box, Button, Grid, Paper } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/store/configureStore";
import AppAutocomplete from "../../app/components/AppAutocomplete";
import AppTextInput from "../../app/components/AppTextInput";
import { MapTitlesToAutocompleteItems } from "../../app/utils/convertStaticDataToDropdownItems";
import AppTextareaInput from "../../app/components/AppTextareaInput";
import EventsFormModel from "../../app/models/events/eventsFormModel";
import { ApiRequestStatus } from "../../app/enums/apiRequestStatus";
import LoadingComponent from "../../app/layout/LoadingComponent";
import { API_REQUEST_FULLFILLED, MESSAGE_DETAILS_SAVED, MESSAGE_NEXT, MESSAGE_PROCESSING_DETAILS, MESSAGE_SAVE_AND_CONTINUE } from "../../app/utils/constant";
import { yupResolver } from "@hookform/resolvers/yup";
import { ApplicantDetailsValidationSchemas } from "./common/eventApprovalValidationSchemas";
import { useReferenceData } from "../../app/customHooks/useReferenceData";
import { useGetPersonalDetails } from "../../app/customHooks/useGetPersonalDetails";
import { PersonalDetailsDto } from "../../app/models/person/personalDetailsDto";
import { useMsal } from "@azure/msal-react";
import { UserClaims } from "../../app/enums/userClaims";
import { toast } from "react-toastify";
import { updatePersonalDetailsStateAsync, getPersonalDetailsAsync } from "../../app/slices/personSlice";

const { Title, FirstName, Surname, Email, MobileNumber, WorkplaceAddress } = EventsFormModel.personalDetailsFormField;

interface Props {
    handleNext: () => void;
}

export function ApplicantDetails({ handleNext }: Props) {
    const methods = useForm({
        mode: 'all',
        resolver: yupResolver(ApplicantDetailsValidationSchemas)
    });
    const formObj = methods.watch();
    const { control, reset, trigger, formState: { isDirty } } = methods;

    const { staticDataStateStatus } = useReferenceData();
    const { personalDetailsStatus } = useGetPersonalDetails();

    const { titles } = useAppSelector(state => state.ReferenceData);
    const { personalDetails, updatePersonalDetailsStatus } = useAppSelector(state => state.personalDetail);

    useEffect(() => {
        if (personalDetails !== undefined && personalDetails !== null
            && titles && titles?.length > 0) {
            reset({
                [Title.name]: (personalDetails.titleId === undefined || personalDetails.titleId === null)
                    ? '' : personalDetails.titleId,
                [FirstName.name]: (personalDetails.firstName === undefined || personalDetails.firstName === null)
                    ? '' : personalDetails.firstName,
                [Surname.name]: (personalDetails.lastName === undefined || personalDetails.lastName === null)
                    ? '' : personalDetails.lastName,
                [Email.name]: (personalDetails.preferredEmail === undefined || personalDetails.preferredEmail === null)
                    ? '' : personalDetails.preferredEmail,
                [MobileNumber.name]: (personalDetails.mobile === undefined || personalDetails.mobile === null)
                    ? '' : personalDetails.mobile,
                [WorkplaceAddress.name]: (personalDetails.postalAddress === undefined || personalDetails.postalAddress === null)
                    ? '' : personalDetails.postalAddress
            });
        }
    }, [personalDetails, titles, reset]);

    const { accounts } = useMsal();

    useEffect(() => {
        if (accounts.length > 0 && accounts[0]?.idTokenClaims) {
            setPersonId(accounts[0].idTokenClaims[UserClaims.EXTENSION_PORTALCRM_PERSONID] as string);
        }
    }, [accounts]);

    const [personId, setPersonId] = useState('');

    const MapToPersonalDetails = (values: any) => {
        const personalDetailsObj: PersonalDetailsDto = {
            ...personalDetails,
            id: personId,
            titleId: values?.Title,
            mobile: values?.MobileNumber,
            postalAddress: values?.WorkplaceAddress
        }

        return personalDetailsObj;
    }

    const dispatch = useAppDispatch();

    const handleSaveAsync = async () => {
        const isStepValid = await trigger();
        if (!isStepValid) trackPortalEvent(portalScreenContext('ApplicantDetails'), 'ValidationBlocked', { step: 'applicant' });

        if (isStepValid && isDirty === true) {
            dispatch(updatePersonalDetailsStateAsync(MapToPersonalDetails(formObj)))
                .then((response) => {
                    if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                        dispatch(getPersonalDetailsAsync(personId)).then((personalDetailsResponse) => {
                            if (personalDetailsResponse.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                                toast.success(MESSAGE_DETAILS_SAVED);
                                trackPortalEvent(portalScreenContext('ApplicantDetails'), 'ApplicationSubmitted', { step: 'applicant' });
                                handleNext();
                            }
                        });
                    }
                });
        }

        if (isStepValid && isDirty === false) {
            handleNext();
        }
    }

    usePortalScreen(portalScreenContext('ApplicantDetails'), undefined, !(staticDataStateStatus === ApiRequestStatus.Pending || personalDetailsStatus === ApiRequestStatus.Pending ||
        updatePersonalDetailsStatus === ApiRequestStatus.Pending));

    if (staticDataStateStatus === ApiRequestStatus.Pending || personalDetailsStatus === ApiRequestStatus.Pending ||
        updatePersonalDetailsStatus === ApiRequestStatus.Pending) {
        return <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />
    }

    return <FormProvider {...methods}>
        <Box sx={{ width: '100%' }} component={Paper}>

            <Grid container rowSpacing={4} sx={{ m: "2%" }}>

                <Grid size={{ xs: 12 }}></Grid>

                <Grid size={{ xs: 12 }}>
                    <AppAutocomplete control={control} name={Title.name}
                        label={Title.label} items={MapTitlesToAutocompleteItems(titles ?? [])} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <AppTextInput name={FirstName.name} label={FirstName.label}
                        disabled={true} maxStringCount={FirstName.maxStringCount} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <AppTextInput name={Surname.name} label={Surname.label} disabled={true}
                        maxStringCount={Surname.maxStringCount} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <AppTextInput control={control} name={Email.name} label={Email.label} disabled={true}
                        maxStringCount={Email.maxStringCount} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <AppTextInput control={control} name={MobileNumber.name} label={MobileNumber.label}
                        maxStringCount={MobileNumber.maxStringCount} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <AppTextareaInput control={control} name={WorkplaceAddress.name} label={WorkplaceAddress.label}
                        maxStringCount={WorkplaceAddress.maxStringCount} />
                </Grid>

                <Grid size={{ xs: 11 }} sx={{ textAlign: 'end' }}>
                    <Button variant="contained" color="primary" onClick={handleSaveAsync}>
                        {isDirty === true ? MESSAGE_SAVE_AND_CONTINUE : MESSAGE_NEXT}
                    </Button>
                </Grid>

                <Grid size={{ xs: 1 }}></Grid>

                <Grid size={{ xs: 12 }}></Grid>

            </Grid>
        </Box>
    </FormProvider>
}